import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRIVATE_PATH } from '@/constants';
import { Space, Button, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { EllipsisOutlined } from '@ant-design/icons';
import type { MenuItems, ActionsProps } from './Actions.types';
import { useActions } from './hooks/useActions';
import { PoNumberModal } from './components/PoNumberModal';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const Actions: FC<ActionsProps> = ({ id, edit, status, campaignName, poNumber }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { menuItems, canUpdate } = useActions({ id, status, campaignName });
  const [isPoOpen, setIsPoOpen] = useState(false);

  Button.defaultProps = { type: 'link', onClick: (e) => e.stopPropagation() };

  const items: MenuItems = useMemo(() => {
    // Keep the destructive Delete action last; other items (e.g. Save as .pdf)
    // stay in their original order between Edit and Raise IO.
    const isDelete = (item: NonNullable<MenuItems>[number]) =>
      Boolean(item && 'danger' in item && item.danger);
    const otherItems = (menuItems ?? []).filter((item) => !isDelete(item));
    const deleteItems = (menuItems ?? []).filter(isDelete);

    return [
      ...(canUpdate
        ? [
            {
              key: 'edit',
              label: t('edit'),
              onClick: ({ domEvent }) => {
                domEvent.stopPropagation();
                edit(id);
              }
            }
          ]
        : []),
      ...otherItems,
      {
        key: 'raise-io',
        label: <span style={{ color: APP_SHELL_THEME.accentText }}>{t('raiseIo')}</span>,
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          navigate(`${PRIVATE_PATH.MEDIA_PLAN}/${id}/raise-io`);
        }
      },
      {
        // POs commonly arrive after the IO is signed, so this stays available
        // for the whole life of the plan rather than only before raising.
        key: 'po-number',
        label: t('poNumberAction'),
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          setIsPoOpen(true);
        }
      },
      ...deleteItems
    ];
  }, [canUpdate, menuItems, edit, id, t, navigate]);

  return (
    <Space size={0}>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button
          aria-label={t('more')}
          icon={<EllipsisOutlined style={{ fontSize: 20 }} />}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
      {/* The modal portals to <body>, but React events still bubble through the
          component tree to the row's onClick — without this, clicking into the
          PO field navigated to the media plan. */}
      <span onClick={(e) => e.stopPropagation()}>
        <PoNumberModal
          open={isPoOpen}
          onClose={() => setIsPoOpen(false)}
          mediaPlanId={id}
          poNumber={poNumber}
        />
      </span>
    </Space>
  );
};

export default Actions;
